import { useState } from 'react';

import { MenuItem, SelectChangeEvent } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { RouterParams } from 'containers/AppRouter';
import InputForm from 'shared/components/Input/InputForm';
import { ACKStatusT } from 'shared/models/schc-templates';
import SCHCTemplate from 'shared/models/SCHCTemplate';

import FragmentationRenderFields from './SCHCTemplatesRuleFormRenderFields.fragmentation';
import FragmentationRenderM from './SCHCTemplatesRuleFormRenderFields.fragmentation.model';

interface SCHCTemplatesRuleFormFragmentationProps {
  index: number;
}
function SCHCTemplatesRuleFormFragmentation({ index }: SCHCTemplatesRuleFormFragmentationProps) {
  const {
    getValues,
    control,
    formState: {
      errors,
    },
  } = useFormContext<SCHCTemplate.Form.ValuesT>();
  const { schcTemplateId } = useParams() as RouterParams;
  const isEdit = schcTemplateId !== undefined;
  const getNewACKStatus = (newStatus: string | undefined): ACKStatusT => {
    switch (newStatus) {
      case SCHCTemplate.API.Fragmentation.Mode.NoACK:
        return ({ isACK: false, isACKError: false, isOptimizedACK: false });
      case SCHCTemplate.API.Fragmentation.Mode.ACKAlways:
        return ({ isACK: true, isACKError: false, isOptimizedACK: false });
      case SCHCTemplate.API.Fragmentation.Mode.ACKOnError:
        return ({ isACK: true, isACKError: true, isOptimizedACK: false });
      case SCHCTemplate.API.Fragmentation.Mode.OptimizedACKOnError:
        return ({ isACK: true, isACKError: false, isOptimizedACK: true });
      default:
        return ({ isACK: false, isACKError: false, isOptimizedACK: false });
    }
  };
  const [ackStatus, setACKStatus] = useState<ACKStatusT>(
    getNewACKStatus(isEdit ? getValues().rules[index].fragmentation?.mode : undefined),
  );
  const onChangeACKStatus = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setACKStatus(getNewACKStatus(value));
  };
  const getNewQosStatus = (newStatus: string | undefined): boolean => {
    switch (newStatus) {
      case 'FRAG_UPLINK':
        return true;
      default:
        return false;
    }
  };
  const [qosStatus, setQosStatus] = useState<boolean>(
    getNewQosStatus(isEdit ? getValues().rules[index].fragmentation?.direction : undefined),
  );
  const onChangeQosStatus = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setQosStatus(getNewQosStatus(value));
  };
  const setDefaultQosValue = () => {
    if (getValues().rules[index]?.fragmentation?.qos) {
      return getValues().rules[index]?.fragmentation?.qos;
    }
    return 'Undefined';
  };
  return (
    <>
      <div>
        <InputForm.Select
          fullWidth
          sx={{ mt: 1, mb: 1 }}
          label="Direction"
          name={`rules.${index}.fragmentation.direction`}
          control={control}
          hasError={
            Array.isArray(errors.rules)
            && !!(errors.rules[index] || [])?.fragmentation?.direction
          }
          defaultValue={getValues().rules[index].fragmentation?.direction}
          onChange={onChangeQosStatus}
        >
          {
            FragmentationRenderM.directionList.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '1rem' }}>
                  {item.icon}
                  {item.label}
                </div>
              </MenuItem>
            ))
          }
        </InputForm.Select>
        <FragmentationRenderFields index={index} fields={FragmentationRenderM.defaultFields} />
        {
          qosStatus
          && (
          <InputForm.Select
            fullWidth
            sx={{ mt: 1, mb: 1 }}
            label="Quality of Service (optional)"
            name={`rules.${index}.fragmentation.qos`}
            control={control}
            hasError={
              Array.isArray(errors.rules)
              && !!errors.rules[index]?.fragmentation?.qos
            }
            defaultValue={setDefaultQosValue()}
          >
            {
            FragmentationRenderM.qosList.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))
          }
          </InputForm.Select>
          )
        }
      </div>
      <div>
        <InputForm.Select
          fullWidth
          sx={{ mt: 1, mb: 1 }}
          label="Fragmentation Mode"
          name={`rules.${index}.fragmentation.mode`}
          control={control}
          hasError={
            Array.isArray(errors.rules)
            && !!errors.rules[index]?.fragmentation?.mode
          }
          defaultValue={getValues().rules[index]?.fragmentation?.mode}
          onChange={onChangeACKStatus}
        >
          {
            FragmentationRenderM.modeList.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))
          }
        </InputForm.Select>
        <div className="fields-side-line">
          {
            ackStatus.isACK
            && <FragmentationRenderFields index={index} fields={FragmentationRenderM.ACKFields} />
          }
          {
            ackStatus.isACK && (!ackStatus.isACKError || !ackStatus.isOptimizedACK)
            && (
              <FragmentationRenderFields
                index={index}
                fields={FragmentationRenderM.ACKAlwaysFields}
              />
            )
          }
          {
            ackStatus.isACK && ackStatus.isACKError
            && (
              <FragmentationRenderFields
                index={index}
                fields={FragmentationRenderM.ACKOnErrorFields}
              />
            )
          }
          {
            ackStatus.isACK && ackStatus.isOptimizedACK
            && (
              <FragmentationRenderFields
                index={index}
                fields={FragmentationRenderM.OptACKOnErrorFields}
              />
            )
          }
        </div>
      </div>
    </>
  );
}

export default SCHCTemplatesRuleFormFragmentation;
